import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const SYSTEM_PROMPT = {
  role: 'system',
  content: `You are Wilcart AI, a CRM assistant for local service businesses. You help manage leads, jobs, contacts, tasks, and documents. Answer questions about the business data, draft documents, and provide actionable insights. Be concise, professional, and helpful. When asked to draft documents, provide clean formatted text. When asked about data, give clear summaries.`
};

const QUICK_PROMPTS = [
  { label: "Today\'s jobs", prompt: 'Summarize what I should focus on today for my jobs and tasks.' },
  { label: 'New leads', prompt: 'Give me a summary of new leads added this week and how to prioritize them.' },
  { label: 'Draft follow-up', prompt: 'Draft a professional follow-up email for a client after completing a service job.' },
  { label: 'Overdue tasks', prompt: 'What should I do about overdue tasks and follow-ups in my CRM?' },
  { label: 'Revenue report', prompt: "Give me a quick summary of this month\'s revenue and top performing services." },
];

const AskAIWidget = ({ orgId, prominent = false }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const streamingMsgRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-sonnet-4-5-20250929', true);

  useEffect(() => {
    if (error) toast?.error('AI error: ' + (error?.message || 'Something went wrong'));
  }, [error]);

  useEffect(() => {
    if (isLoading && response) {
      setMessages(prev => {
        const last = prev?.[prev?.length - 1];
        if (last?.role === 'assistant' && last?.streaming) {
          return [...prev?.slice(0, -1), { ...last, content: response }];
        }
        return prev;
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    if (!isLoading && response && streamingMsgRef?.current) {
      setMessages(prev => {
        const last = prev?.[prev?.length - 1];
        if (last?.role === 'assistant' && last?.streaming) {
          return [...prev?.slice(0, -1), { role: 'assistant', content: response, streaming: false }];
        }
        return prev;
      });
      setConversationHistory(prev => [...prev, { role: 'assistant', content: response }]);
      streamingMsgRef.current = null;
    }
  }, [isLoading, response]);

  // Only scroll when a NEW message is added (not on streaming updates)
  useEffect(() => {
    const currentCount = messages?.length;
    const isNewMessage = currentCount > prevMessageCountRef?.current;
    prevMessageCountRef.current = currentCount;

    if (isNewMessage) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [messages?.length]);

  const handleSend = useCallback((text) => {
    const msg = text || input?.trim();
    if (!msg || isLoading) return;

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg, { role: 'assistant', content: '', streaming: true }]);
    streamingMsgRef.current = true;

    const newHistory = [...conversationHistory, { role: 'user', content: msg }];
    setConversationHistory(newHistory);

    sendMessage([SYSTEM_PROMPT, ...newHistory], { max_tokens: 1000 });
    setInput('');
  }, [input, isLoading, conversationHistory, sendMessage]);

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setConversationHistory([]);
    prevMessageCountRef.current = 0;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-card border border-border"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(34,197,94,0.06)', minHeight: '220px' }}
    >
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{ background: 'linear-gradient(90deg, #22C55E, #16A34A, #4ade80)' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                boxShadow: '0 2px 8px rgba(34,197,94,0.3)',
              }}
            >
              <Icon name="Sparkles" size={16} color="white" />
            </div>
            {isLoading && (
              <span
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card animate-pulse"
                style={{ background: '#22C55E' }}
              />
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-card-foreground leading-tight">Ask Wilcart AI</h3>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">Your AI-powered CRM assistant</p>
          </div>
        </div>

        {messages?.length > 0 && (
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-lg hover:bg-muted border border-border"
          >
            <Icon name="RotateCcw" size={11} />
            Clear
          </button>
        )}
      </div>

      {/* Chat messages area */}
      {messages?.length > 0 && (
        <div
          className="px-5 pb-3 overflow-y-auto space-y-3"
          style={{ maxHeight: '180px' }}
        >
          {messages?.map((msg, i) => (
            <div key={i} className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
              {msg?.role === 'assistant' && (
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                    boxShadow: '0 1px 4px rgba(34,197,94,0.25)',
                  }}
                >
                  <Icon name="Sparkles" size={11} color="white" />
                </div>
              )}
              <div
                className={`text-sm leading-relaxed rounded-2xl px-3.5 py-2.5 max-w-[78%] ${
                  msg?.role === 'user' ?'text-white rounded-tr-sm' :'text-card-foreground rounded-tl-sm bg-muted border border-border'
                }`}
                style={
                  msg?.role === 'user'
                    ? { background: 'linear-gradient(135deg, #22C55E, #16A34A)', boxShadow: '0 2px 8px rgba(34,197,94,0.2)' }
                    : {}
                }
              >
                {msg?.streaming && !msg?.content ? (
                  <div className="flex items-center gap-1 py-0.5">
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#22C55E', animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#22C55E', animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#22C55E', animationDelay: '300ms' }} />
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{msg?.content}</p>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input area */}
      <div className="px-4 pb-3">
        <div
          className={`relative rounded-xl transition-all duration-200 bg-muted border ${isFocused ? 'border-primary' : 'border-border'}`}
          style={isFocused ? { boxShadow: '0 0 0 3px rgba(34,197,94,0.1)' } : {}}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <Icon name="Sparkles" size={15} color={isFocused ? '#22C55E' : undefined} className={isFocused ? '' : 'text-muted-foreground'} />
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e?.target?.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Ask anything about your business..."
              disabled={isLoading}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50 border-0 ring-0 focus:ring-0 p-0"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input?.trim() || isLoading}
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 disabled:cursor-not-allowed"
              style={{
                background: input?.trim() && !isLoading ? 'linear-gradient(135deg, #22C55E, #16A34A)' : undefined,
                boxShadow: input?.trim() && !isLoading ? '0 2px 8px rgba(34,197,94,0.3)' : 'none',
              }}
            >
              {isLoading ? (
                <svg className="animate-spin w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <Icon
                  name="ArrowUp"
                  size={14}
                  color={input?.trim() && !isLoading ? 'white' : undefined}
                  className={input?.trim() && !isLoading ? '' : 'text-muted-foreground'}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Quick action chips */}
      <div className="px-4 pb-5">
        <div className="flex flex-wrap gap-1.5">
          {QUICK_PROMPTS?.map((qp) => (
            <button
              key={qp?.label}
              onClick={() => handleSend(qp?.prompt)}
              disabled={isLoading}
              className="text-xs font-medium text-muted-foreground hover:text-primary rounded-full px-3 py-1.5 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/10 bg-muted border border-border hover:border-primary/30"
            >
              {qp?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AskAIWidget;
